import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Grid, Typography, Button, Paper, Accordion, AccordionSummary, AccordionDetails, TextField, MenuItem, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { selectedTabContext } from '../../../Context/SelectedTabContext';
import { ShopContext } from '../../../Context/ShopContext';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';
import withConsoleBase from '../../utils/ConsoleBase/withConsoleBase';
import AccountHeadDialog from '../../popups/AccountHeadDialog';
import CashInOutDialog from '../../popups/CashInOutDialog';
import AccountServices from '../../../services/AccountServices';
import { AlertMessagesContext } from 'react-alert-messages';
import { roundOff } from '../../../helpers/NumberFormatHelper';
import LedgerDetailTable from './LedgerDetailTable';
import DateTimeRange from '../../utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from '../../../services/DynamicDateRangeService';
import ShiftService from '../../../services/ShiftService';
import DateTimeHelpers from '../../../helpers/DateTimeHelpers';

function AccountHeadsPage() {
  const { t } = useTranslation('common');
  const { setSelectedTab, setSelectedSubTab, setSections } = useContext(selectedTabContext);
  const { cachedAccountHeads, setCachedAccountHeads, cachedLedgers, setCachedLedgers } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);


  const getGroupLabel = (group) => {
    switch (group) {
      case 'ASSET':
        return t('Asset');
      case 'LIABILITY':
        return t('Liability');
      case 'INCOME':
        return t('Income');
      case 'EXPENSE':
        return t('Expense');
      case 'EQUITY':
        return t('Equity');
      default:
        return group;
    }
  };

  const getAccountTypeLabel = (type) => {
    switch (type) {
      case 'PAYMENT_MODE':
        return t('Payment Mode');
      case 'GENERAL':
        return t('General');
      case 'CUSTOMER':
        return t('Customer');
      case 'SUPPLIER':
        return t('Supplier');
      default:
        return type;
    }
  };

  const [accountHeads, setAccountHeads] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [loading, setLoading] = useState(false);

  const [expandedHead, setExpandedHead] = useState(false);
  const [showCashInOutDialog, setShowCashInOutDialog] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState('all');
  const { startDate: initialStart, endDate: initialEnd } = dynamicDateRange('ACCOUNT_LEDGER');
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);

  const fetchShifts = useCallback(async () => {
    try {
      const params = {};
      if (startDate) params.start_date = DateTimeHelpers.getTimezoneOffsetDate(startDate);
      if (endDate) params.end_date = DateTimeHelpers.getTimezoneOffsetDate(endDate);
      const data = await ShiftService.getShifts({ ...params, limit: 1000 });
      setShifts(data.results || data || []);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
  }, [startDate, endDate, postAlertMessage]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const fetchAccountHeads = useCallback(async (force = false) => {
    if (!force && cachedAccountHeads) {
      setAccountHeads(cachedAccountHeads);
      return;
    }
    setLoading(true);
    try {
      const data = await AccountServices.getAccountHeads();
      const results = data.results || data || [];
      setAccountHeads(results);
      setCachedAccountHeads(results);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoading(false);
  }, [postAlertMessage, cachedAccountHeads, setCachedAccountHeads]);

  useEffect(() => {
    setSelectedTab('account-heads');
    setSelectedSubTab('');
    setSections((prev) => ({ ...prev, management: true }));
    fetchAccountHeads();
  }, [setSelectedTab, setSelectedSubTab, setSections, fetchAccountHeads]);

  return (
    <div style={{ padding: '20px' }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
        <Grid item xs={6}>
          <Typography variant="h5" component="h1">
            {t('Account Heads')}
          </Typography>
        </Grid>
        <Grid item xs={6} style={{ textAlign: 'right' }}>
          <Button
            variant="contained"
            style={{ backgroundColor: '#00a65a', color: '#fff', marginRight: '10px' }}
            onClick={() => setShowCashInOutDialog(true)}
          >
            <SyncIcon style={{ marginRight: '6px', fontSize: '16px' }} />
            {t('Deposit / Withdrawal')}
          </Button>
          <Button variant="contained" style={{backgroundColor: '#ff851b', color: '#fff', }}
          onClick={() => setShowAddDialog(true)}>
            <NoteAddIcon sx={{mr: 0.75, fontSize: '16px', color: 'inherit',}}/>
            {t('Add Account Head')}
          </Button>
        </Grid>
      </Grid>

      {showAddDialog && (
        <AccountHeadDialog
          existingAccounts={accountHeads}
          onClose={() => setShowAddDialog(false)}
          onSuccess={() => {
            setShowAddDialog(false);
            fetchAccountHeads(true);
          }}
        />
      )}

      {editingAccount && (
        <AccountHeadDialog
          existingAccounts={accountHeads}
          accountHead={editingAccount}
          onClose={() => setEditingAccount(null)}
          onSuccess={() => {
            setEditingAccount(null);
            fetchAccountHeads(true);
          }}
        />
      )}

      {showCashInOutDialog && (
        <CashInOutDialog
          defaultAccountHeadId={expandedHead || ''}
          onClose={() => setShowCashInOutDialog(false)}
          onSuccess={() => {
            setShowCashInOutDialog(false);
            fetchAccountHeads(true);
            setCachedLedgers({});
          }}
        />
      )}

      <Paper style={{ padding: '15px', marginBottom: '20px' }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6}>
            <DateTimeRange
              setFilterFromDate={setStartDate}
              setFilterToDate={setEndDate}
              storageKey="ACCOUNT_LEDGER"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <div style={{ paddingBottom: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: 500, color: '#333' }}>{t('Shift')}</label>
            </div>
            <TextField
              select
              variant="outlined"
              size="small"
              fullWidth
              value={selectedShift}
              onChange={(e) => setSelectedShift(e.target.value)}
            >
              <MenuItem value="all">{t('All Shifts')}</MenuItem>
              {shifts.map((shift) => (
                <MenuItem key={shift.id} value={String(shift.id)}>
                  {shift.name ? `${shift.name} (${shift.id})` : `Shift ${shift.id}`}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {loading ? (
           <Typography align="center" style={{ padding: 20 }}>{t('Loading...')}</Typography>
        ) : accountHeads.length === 0 ? (
           <Typography align="center" style={{ padding: 20 }}>{t('No Account Heads Found')}</Typography>
        ) : (
          accountHeads.map((account) => (
            <Accordion
              key={account.id}
              expanded={expandedHead === String(account.id)}
              onChange={(event, isExpanded) => setExpandedHead(isExpanded ? String(account.id) : false)}
              TransitionProps={{ unmountOnExit: true }}
              sx={{
                borderRadius: '12px !important',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                '&:before': { display: 'none' },
                overflow: 'hidden',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#6366f1' }} />}
                sx={{
                  backgroundColor: '#ffffff',
                  padding: '12px 24px',
                  borderBottom: expandedHead === String(account.id) ? '1px solid #f1f5f9' : 'none',
                }}
              >
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1.1rem' }}>
                      {account.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Chip label={getGroupLabel(account.group_name)} size="small" sx={{ backgroundColor: '#e2e8f0', color: '#334155', fontWeight: 500 }} />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Chip label={getAccountTypeLabel(account.type)} size="small" variant="outlined" sx={{ color: '#475569', borderColor: '#cbd5e1' }} />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                      Bal: {roundOff(account.balance || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3} style={{ textAlign: 'right' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingAccount(account);
                      }}
                      sx={{ color: '#6366f1', borderColor: '#6366f1', '&:hover': { borderColor: '#4f46e5', backgroundColor: '#eef2ff' } }}
                    >
                      {t('Edit')}
                    </Button>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: '0', backgroundColor: '#ffffff' }}>
                {expandedHead === String(account.id) && (
                  <LedgerDetailTable 
                    accountHeadId={account.id}
                    startDate={startDate}
                    endDate={endDate}
                    shiftId={selectedShift}
                    accountHeads={accountHeads}
                    cachedLedgers={cachedLedgers}
                    setCachedLedgers={setCachedLedgers}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </div>
    </div>
  );
}

export default withConsoleBase(AccountHeadsPage);
